/**
 * The frame a corner-box drag has to happen in, so smart scaling is correct.
 *
 * {@link createSmartScaleDrive} answers "what size is this drag asking for, and
 * is it still shaping?" — but only if it is HANDED an honest size. Getting that
 * size is the other half of the problem, and it is where the obvious
 * implementation goes wrong in four separate ways. All four are visible in ten
 * seconds of dragging:
 *
 * 1. **It stretched instead of shaping.** The size a drag asks for is the
 *    component's CURRENT size times the drag's ratio. Computing it from the
 *    REST size instead throws away every resize already applied, so a
 *    component sitting at 1.6 m jumps back toward 1.0 and then stretches.
 *    The authored size is the baseline, and rest is only the fallback for a
 *    component that has never been sized ({@link realizedLocalSize}).
 *
 * 2. **It moved the opposite side too.** Babylon's `BoundingBoxGizmo` scales
 *    about the centre and shifts the node to compensate. Dragging one face
 *    therefore grew the component both ways. Resizing has to feel anchored:
 *    the face opposite the handle stays where it is. That is what the position
 *    mirror in {@link update} restores.
 *
 * 3. **Rotation made it worse.** Attaching the gizmo to the object's own node
 *    puts the box on the world-axis-aligned bounds, which for a rotated
 *    component is wrong twice over: the AABB of a tilted box is bigger than the
 *    box, and its axes no longer line up with the component's own — so "drag
 *    this side" moved the wrong dimension, and the size handed to MorphStretch
 *    was the AABB's rather than the component's.
 *
 * 4. **Undo left it displaced.** A size drag changes two things — the
 *    behavior's size, and the object's position (because of the anchoring in
 *    2) — so both belong in ONE entry, or ⌘Z restores the size and leaves the
 *    component parked where the drag pushed it.
 *
 * The fix for 2 and 3 is the same object: the gizmo drives an invisible PROXY
 * built in the component's own frame — its world rotation, and its realized
 * size scaled into world units. Babylon then drags along the component's own
 * axes, `proxy.scaling / worldScale` is exactly the local size MorphStretch
 * speaks, and the proxy's own recentring is the shift to mirror back.
 *
 * This lives in the engine for the reason the whole feature does: every number
 * here comes from what the component reports about itself, so a host that
 * re-derived it would be re-deriving the rig's arithmetic — and getting it
 * wrong in exactly these four ways, which is what happened.
 */

import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import "@babylonjs/core/Meshes/Builders/boxBuilder";
import type { Mesh } from "@babylonjs/core/Meshes/mesh";
import { Vector3, Matrix, Quaternion } from "@babylonjs/core/Maths/math.vector";
import type { Scene } from "@babylonjs/core/scene";

import type { SceneObject } from "../core/SceneObject";
import { MorphStretch } from "../behaviors/morphstretch";
import { TransformCommand } from "./undo/TransformCommand";
import type { TransformSnapshot } from "./undo/TransformCommand";
import { CompositeCommand } from "./undo/CompositeCommand";
import type { UndoCommand } from "./undo/UndoCommand";
import { createSmartScaleDrive, morphStretchOf, shownSize, type SmartScaleBox, type SmartScaleDrive } from "./smartScale";
import { Logger } from "../utils/Logger";

/** Below this a world scale is degenerate rather than something to divide by. */
const MIN_DIVISOR = 1e-4;

interface WorldBasis {
    /** World rotation, with translation and scale stripped. */
    rotation: Matrix;
    /** Per-axis world scale. */
    scale: Vector3;
}

/**
 * Split a world matrix into rotation and scale.
 *
 * The rows of the upper 3×3 are the node's world axes: their lengths are the
 * world scale, their normalized forms the rotation.
 *
 * Assumes no shear: per-row normalisation is exact for rotation × scale,
 * which is every frame a box gizmo can draw handles for. A component under
 * an ancestor that is both rotated and non-uniformly scaled has a sheared
 * world frame and no face-aligned box at all — out of scope by the same
 * token as for the plain gizmo.
 */
function decomposeWorldBasis(world: Matrix): WorldBasis {
    const m = world.m;
    const rows = [
        new Vector3(m[0], m[1], m[2]),
        new Vector3(m[4], m[5], m[6]),
        new Vector3(m[8], m[9], m[10]),
    ];
    const scale = new Vector3(rows[0].length(), rows[1].length(), rows[2].length());
    const axes = rows.map((row, i) => {
        const len = [scale.x, scale.y, scale.z][i];
        return len > MIN_DIVISOR ? row.scale(1 / len) : row.clone();
    });
    return {
        rotation: Matrix.FromValues(
            axes[0].x, axes[0].y, axes[0].z, 0,
            axes[1].x, axes[1].y, axes[1].z, 0,
            axes[2].x, axes[2].y, axes[2].z, 0,
            0, 0, 0, 1,
        ),
        scale,
    };
}

/**
 * The size MorphStretch is currently realizing on each axis, in local metres.
 *
 * The box a Layout resolved for the piece first (see `shownSize`). Then the
 * AUTHORED size: it is what the behavior is holding, so it is the baseline a
 * drag scales from. Rest is the fallback for an axis never sized — using it
 * unconditionally is what made a drag snap an already-resized component back
 * toward its rest size and then stretch from there.
 */
function realizedLocalSize(stretch: MorphStretch): Vector3 | null {
    const reach = stretch.getStretchCapability();
    if (!reach) return null;
    const size = new Vector3(
        shownSize(stretch, "width", reach.X?.rest) ?? 0,
        shownSize(stretch, "height", reach.Y?.rest) ?? 0,
        shownSize(stretch, "depth", reach.Z?.rest) ?? 0,
    );
    return [size.x, size.y, size.z].every((v) => Number.isFinite(v) && v > 0) ? size : null;
}

/**
 * The world-space centre of the geometry a SceneObject OWNS. Child objects
 * parent under this transform, so an unpruned walk would centre the proxy on
 * the union of the component and its attachments — and every handle would
 * then sit off the component's real faces.
 *
 * It is the bounds centre, which is the right pivot for a BOX gizmo: the
 * handles sit on the faces of the component's bounds, and the anchoring in
 * `update` carries the box's shift onto the object as a delta, so an
 * off-centre pivot or an L-shaped shell still grows from the grabbed face.
 */
function worldCentre(object: SceneObject): Vector3 | null {
    const meshes = object.getOwnedMeshes();
    if (meshes.length === 0) return null;
    let min: Vector3 | null = null;
    let max: Vector3 | null = null;
    for (const mesh of meshes) {
        mesh.computeWorldMatrix(true);
        const box = mesh.getBoundingInfo().boundingBox;
        min = min ? Vector3.Minimize(min, box.minimumWorld) : box.minimumWorld.clone();
        max = max ? Vector3.Maximize(max, box.maximumWorld) : box.maximumWorld.clone();
    }
    return min && max ? Vector3.Center(min, max) : null;
}

export interface SmartScaleRig {
    /**
     * The mesh a host's box gizmo must be attached to while a smart component
     * is selected. Null when the selection cannot be smart-scaled, in which
     * case the host attaches its gizmo the ordinary way.
     *
     * A MESH, not a bare TransformNode: `BoundingBoxGizmo` sizes its handles
     * from the attached node's bounding info, and a TransformNode has none —
     * attach one and the gizmo silently draws nothing at all. It is an
     * invisible unit cube, so its scaling IS its world size.
     */
    readonly proxy: Mesh | null;
    /** True while a drag is being routed into MorphStretch. */
    readonly active: boolean;
    /** Fit the proxy to this selection and paint the box. Returns false when
     *  the object is not a smart component. */
    attach(object: SceneObject | null | undefined): boolean;
    /** Drag start: snapshot what `update` measures against. */
    begin(): void;
    /** One drag tick. Reads the proxy, writes the size, plants the anchor. */
    update(): boolean;
    /** End the gesture. Returns ONE entry covering size and position. */
    end(): UndoCommand | null;
    /** Re-fit the proxy after something else moved or resized the object. */
    refresh(): void;
    dispose(): void;
}

export interface SmartScaleRigOptions {
    /**
     * Called when a gesture finishes, with its undo entry (or null when
     * nothing changed). The host pushes it.
     *
     * Required for correctness, not convenience: a gesture can end WITHOUT the
     * host's drag-end hook firing at all. Babylon reaches `releaseDrag()` only
     * from its scene's own pointer observable
     * (`pointerDragBehavior.js:240-246`), and a gizmo handle lives in the
     * utility-layer scene, which has `detachControl()` and is fed purely by
     * forwarding the original scene's pointer events. Release the button
     * outside the canvas and no POINTERUP is ever forwarded — so
     * `onScaleBoxDragEndObservable` never fires, no entry is pushed, and the
     * rig stays mid-gesture into the next drag. This is the path that catches
     * it.
     */
    onGestureEnd?: (command: UndoCommand | null) => void;
}

/**
 * @param scene - the scene the proxy lives in.
 * @param getBox - the gizmo to paint, read lazily (hosts recreate theirs).
 */
export function createSmartScaleRig(
    scene: Scene,
    getBox: () => SmartScaleBox | null,
    options: SmartScaleRigOptions = {},
): SmartScaleRig {
    const drive: SmartScaleDrive = createSmartScaleDrive(getBox);
    let proxy: Mesh | null = null;
    let object: SceneObject | null = null;
    /** World scale the proxy was built with — divides its scaling back to local. */
    let proxyWorldScale: Vector3 | null = null;
    let start: { proxyScale: Vector3; proxyPos: Vector3; objAbsPos: Vector3 } | null = null;
    let before: TransformSnapshot | null = null;
    /** The host's own drag gain, restored when this rig lets the box go. */
    let hostDragSpeed: number | undefined;
    /** Armed for the duration of a gesture; see `SmartScaleRigOptions.onGestureEnd`. */
    let strandedRelease: (() => void) | null = null;

    /**
     * A size drag has to track the handle 1:1.
     *
     * Babylon's `scaleDragSpeed` defaults to 0.2, so the box travels five times
     * further than the geometry — "I dragged to the edge and it barely grew".
     * That is tolerable for a plain scale, where the user is nudging a
     * multiplier, and wrong for a size, where the handle is a statement about
     * where the component should END.
     *
     * Set while a smart component is attached and PUT BACK when it is not, so a
     * host that deliberately chose a different feel for ordinary scaling keeps
     * it — the engine is claiming this gesture, not the gizmo.
     */
    const claimDragSpeed = (): void => {
        const box = getBox();
        if (!box || hostDragSpeed !== undefined) return;
        hostDragSpeed = box.scaleDragSpeed;
        box.scaleDragSpeed = 1;
    };
    const releaseDragSpeed = (): void => {
        const box = getBox();
        if (hostDragSpeed === undefined) return;
        if (box) box.scaleDragSpeed = hostDragSpeed;
        hostDragSpeed = undefined;
    };

    const ensureProxy = (): Mesh => {
        if (!proxy || proxy.isDisposed()) {
            // A unit cube, so `scaling` reads directly as world size — and a
            // real mesh, because BoundingBoxGizmo measures the node it is given
            // and draws nothing for one with no bounding info.
            proxy = MeshBuilder.CreateBox("__smartScaleProxy__", { size: 1 }, scene);
            proxy.isVisible = false;
            proxy.isPickable = false;
            // Marked the way the editor marks its own helpers: the hosts' own
            // gizmos and scene trees read `metadata.editorHandle` to skip it,
            // and `doNotSerialize` is Babylon's flag, read by its serializers.
            proxy.metadata = { editorHandle: true, doNotSerialize: true, hidden: true };
            proxy.doNotSerialize = true;
        }
        return proxy;
    };

    const fit = (obj: SceneObject, stretch: MorphStretch): boolean => {
        const size = realizedLocalSize(stretch);
        const centre = worldCentre(obj);
        if (!size || !centre) return false;
        let basis: WorldBasis;
        try {
            obj.transform.computeWorldMatrix(true);
            basis = decomposeWorldBasis(obj.transform.getWorldMatrix());
        } catch (err) {
            Logger.warn(`[smartScaleRig] could not read the component's frame: ${String(err)}`);
            return false;
        }
        const node = ensureProxy();
        node.parent = null;
        node.position.copyFrom(centre);
        // The component's OWN rotation, so the handles lie on its faces rather
        // than on a world-aligned box drawn around it.
        node.rotationQuaternion = Quaternion.FromRotationMatrix(basis.rotation);
        node.scaling.set(
            size.x * basis.scale.x,
            size.y * basis.scale.y,
            size.z * basis.scale.z,
        );
        node.computeWorldMatrix(true);
        proxyWorldScale = basis.scale.clone();
        return true;
    };

    /**
     * Finish a gesture that is still running, and hand its entry to the
     * host — the same contract as a release the gizmo never saw.
     *
     * Every tick has already written MorphStretch data through `commit`, so
     * an interrupted gesture is a real, applied change. Tearing the rig down
     * (or re-pointing it) without this leaves that change on screen and
     * absent from the undo stack.
     */
    const finishActive = (): void => {
        if (!drive.active) return;
        options.onGestureEnd?.(rig.end());
    };

    const rig: SmartScaleRig = {
        get proxy() { return proxy; },
        get active() { return drive.active; },

        attach(obj) {
            // Re-pointing the rig mid-drag abandons the gesture in flight; its
            // committed ticks still need their entry (see `begin`).
            finishActive();
            object = obj ?? null;
            const stretch = obj ? morphStretchOf(obj) : null;
            if (!obj || !stretch || !stretch.getStretchCapability()) {
                proxyWorldScale = null;
                releaseDragSpeed();
                drive.paintForSelection(obj);
                return false;
            }
            if (!fit(obj, stretch)) {
                proxyWorldScale = null;
                releaseDragSpeed();
                drive.paintForSelection(null);
                return false;
            }
            claimDragSpeed();
            drive.paintForSelection(obj);
            return true;
        },

        refresh() {
            const stretch = object ? morphStretchOf(object) : null;
            if (object && stretch) fit(object, stretch);
        },

        begin() {
            if (!proxy || !object) return;
            // A gesture already running means this one interrupts it — a
            // selection change from the outliner or a keyboard shortcut while
            // the pointer is still down. Its ticks are already committed to
            // MorphStretch, so finishing it here is what keeps them undoable;
            // dropping through would overwrite the drive's `before` and strand
            // them with no entry.
            finishActive();
            drive.begin(object);
            // The behavior can go away between attach and begin; then there
            // is no gesture, and nothing to net.
            if (!drive.active) return;
            start = {
                proxyScale: proxy.scaling.clone(),
                proxyPos: proxy.position.clone(),
                objAbsPos: object.transform.getAbsolutePosition().clone(),
            };
            before = TransformCommand.capture(object);
            // The safety net. Whichever release the browser actually delivers —
            // the gizmo's own, or this one — finishes the gesture exactly once,
            // because `end()` clears the state both read.
            const win = typeof window === 'undefined' ? null : window;
            if (win) {
                const finish = (): void => {
                    strandedRelease?.();
                    if (!drive.active) return;
                    options.onGestureEnd?.(rig.end());
                };
                const off = (): void => {
                    win.removeEventListener('pointerup', finish);
                    win.removeEventListener('pointercancel', finish);
                    strandedRelease = null;
                };
                strandedRelease = off;
                win.addEventListener('pointerup', finish);
                win.addEventListener('pointercancel', finish);
            }
        },

        update() {
            if (!proxy || !proxyWorldScale || !start || !object) return false;
            // Deleted under the pointer — a scripted delete, another client.
            // Nothing to write to and nothing to move; `end` drops the gesture.
            if (object.transform.isDisposed()) return false;
            const safe = (v: number, d: number): number => (Math.abs(d) > 1e-6 ? v / d : v);
            const consumed = drive.update({
                x: safe(proxy.scaling.x, proxyWorldScale.x),
                y: safe(proxy.scaling.y, proxyWorldScale.y),
                z: safe(proxy.scaling.z, proxyWorldScale.z),
            });
            if (!consumed) return false;
            // `consumed` means the drive WROTE a size, not that the behavior
            // accepted it — the commit settles later. The anchor can still
            // trust it: the drive only writes what `requestedSize` admits,
            // positive finite numbers, the exact domain `MorphStretch.validate`
            // accepts. A refusal is logged by the drive; it is not expected.
            //
            // Plant the anchor. Babylon scales the proxy about the grabbed
            // handle's OPPOSITE face by moving the proxy, so carrying that
            // shift onto the object is what keeps that face still and grows
            // the component one-sided. Without it a drag on one side opens
            // both, which reads as the component's centre moving.
            //
            // Scaling is deliberately NOT mirrored: MorphStretch is realizing
            // the size on the meshes, and multiplying the node on top would
            // apply the gesture twice per tick.
            object.transform.setAbsolutePosition(
                start.objAbsPos.add(proxy.position.subtract(start.proxyPos)),
            );
            object.transform.computeWorldMatrix(true);
            return true;
        },

        end() {
            strandedRelease?.();
            const obj = object;
            const wasBefore = before;
            start = null;
            before = null;
            if (obj?.transform.isDisposed()) {
                // The object went away mid-drag. Its deletion owns the undo
                // story now; an entry sizing a node that no longer exists
                // would only undo into nothing. Clear the drive and drop it.
                try { drive.end(); } catch { /* the behavior went with the object */ }
                return null;
            }
            const sizeCommand = drive.end();
            if (!obj || !wasBefore) return sizeCommand;

            // One gesture, one entry. The anchoring above MOVED the object, so
            // an entry carrying only the size would undo to the right size in
            // the wrong place.
            const after = TransformCommand.capture(obj);
            const moved = (['position', 'rotation', 'scale'] as const).some((key) =>
                wasBefore[key].some((v, i) => Math.abs(v - after[key][i]) > 1e-6));
            const parts: UndoCommand[] = [];
            if (moved) parts.push(new TransformCommand(obj, wasBefore, after));
            if (sizeCommand) parts.push(sizeCommand);
            if (parts.length === 0) return null;
            if (parts.length === 1) return parts[0];
            return new CompositeCommand(`Resize ${obj.name}`, parts);
        },

        dispose() {
            // Component unmount, panel close, mode switch — all reachable with
            // the pointer still down.
            finishActive();
            strandedRelease?.();
            releaseDragSpeed();
            proxy?.dispose();
            proxy = null;
            object = null;
            proxyWorldScale = null;
            start = null;
            before = null;
        },
    };
    return rig;
}
